// Caché de lecturas con revalidación en segundo plano.
//
// El problema que resuelve: la app desmonta y vuelve a montar las vistas al
// navegar entre pestañas, y cada montaje repetía la misma llamada. Con
// endpoints de segundos eso se siente como que la app "carga a cada rato".
//
// Con esto, volver a una vista pinta lo último que se sabía al instante y
// revalida por detrás. Nunca se muestra un spinner sobre datos que ya están.

const entries = new Map();
const inflight = new Map();

/** Lo cacheado para esa clave, o `undefined` si no hay nada. */
export const peek = (key) => entries.get(key)?.value;

/** Si lo cacheado sigue siendo fresco para la edad que pide quien lee. */
export const isFresh = (key, maxAgeMs) => {
  const entry = entries.get(key);
  if (!entry) return false;
  if (maxAgeMs === Infinity) return true;
  return Date.now() - entry.at < maxAgeMs;
};

/**
 * Lee con caché.
 *
 * @param {string} key clave estable de la lectura
 * @param {Function} loader función sin argumentos que devuelve una promesa
 * @param {Object} options `{maxAgeMs, force}` — `maxAgeMs: Infinity` para lo
 *   que no cambia nunca (el grafo de una versión congelada, por ejemplo)
 * @returns {Promise<*>} el valor, del caché o recién traído
 */
export const swr = (key, loader, { maxAgeMs = 30000, force = false } = {}) => {
  if (!force && isFresh(key, maxAgeMs)) {
    return Promise.resolve(entries.get(key).value);
  }

  // Dos montajes simultáneos de la misma vista comparten la llamada en vuelo
  // en vez de disparar dos.
  const running = inflight.get(key);
  if (running && !force) return running;

  const promise = Promise.resolve()
    .then(loader)
    .then((value) => {
      entries.set(key, { value, at: Date.now() });
      return value;
    })
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
};

/** Borra una clave, o las que empiecen con un prefijo. */
export const invalidate = (keyOrPrefix, { prefix = false } = {}) => {
  if (!prefix) {
    entries.delete(keyOrPrefix);
    return;
  }
  for (const key of [...entries.keys()]) {
    if (key.startsWith(keyOrPrefix)) entries.delete(key);
  }
};

/** Vacía todo. Existe para los tests y para el cierre de sesión. */
export const clearCache = () => {
  entries.clear();
  inflight.clear();
};
